<script lang="ts">
  import type { BookingMethodsSubscription } from '@bruce/api/common/bookingMethods'
  import MembershipCard from './MembershipCard.svelte'
  import type { StudioIncludes } from '@bruce/api/api/studio/response'
  import { Button, ToggleButtonGroup, type Language } from '@bruce/components'
  import FeaturedStudioGrid from './FeaturedStudioGrid.svelte'
  import type { Tier, TierId } from '@bruce/api/common/tier'
  import { browser } from '$app/environment'
  import { page } from '$app/stores'
  import { STRINGS } from '$lib/constants/strings'
  import { utils } from '$lib/utils'
  import type { User } from '@bruce/api/common/user'
  import type { CityCampaign } from '$lib/types/campaign'
  import type { CityIncludesWithToken } from '@bruce/api/api/location/city/response'
  import type { ContractType } from '$lib/types/contract'

  export let subscriptions: BookingMethodsSubscription[]
  export let currency: string | undefined = undefined
  export let tiers: Tier[]
  export let trial = false
  export let user: User | undefined = undefined
  export let imageUrl: string | undefined = undefined
  export let featuredStudios:
    | Record<
        string,
        StudioIncludes<{
          params: object
        }>[]
      >
    | undefined = undefined
  export let campaign: CityCampaign | undefined = undefined
  export let currentCity:
    | CityIncludesWithToken<{
        params: {
          include_country: true
          include_booking_methods: true
          locale: Language
        }
      }>
    | undefined = undefined
  export let selectedTier: Tier | undefined = undefined
  export let contractType: ContractType

  let arrowOffset = 0
  let membershipSelect: HTMLDivElement | undefined = undefined
  let membershipCard: Partial<Record<TierId, HTMLDivElement>> = {}
  let tierFeaturedStudios:
    | StudioIncludes<{
        params: object
      }>[]
    | undefined = undefined
  let campaignTierName =
    campaign?.tierName && !campaign?.tierName?.includes('Base') // Only set a new default tier if Base is not in the campaign
      ? campaign?.tierName?.[0]
      : undefined
  let campaignTierId =
    campaignTierName && tiers.find((tier) => tier.name === campaignTierName)?.id
  let tier: TierId = campaignTierId || 1

  $: setArrowPosition(tier, membershipCard)
  $: activeTierButton = getActiveTierButton(tier, membershipSelect)
  $: lang = $page.params.lang as Language
  $: updateTier(tier, featuredStudios)

  $: if ($page.status || subscriptions) {
    resetTier()
  }

  function getActiveTierButton(
    tier: TierId,
    membershipSelect: HTMLDivElement | undefined,
  ) {
    if (membershipSelect) {
      const buttons = membershipSelect.querySelectorAll('button')

      const button = Array.from(buttons).find(
        (button) =>
          button.textContent?.trim() === tiers.find((t) => t.id === tier)?.name,
      )

      return button
    }
  }

  function setArrowPosition(
    tier: TierId,
    membershipCard: Partial<Record<TierId, HTMLDivElement>>,
  ) {
    if (browser) {
      const card = membershipCard[tier]

      if (card) {
        arrowOffset =
          card.offsetLeft - window.innerWidth / 2 + card.offsetWidth / 2
      }
    }
  }

  function updateTier(
    tier: TierId,
    featuredStudios:
      | Record<
          string,
          StudioIncludes<{
            params: object
          }>[]
        >
      | undefined,
  ) {
    tierFeaturedStudios = featuredStudios?.[tier]
    selectedTier = tiers.find((t) => t.id === tier)
  }

  function resetTier() {
    const availableTiers = subscriptions.map(
      (method) => method.subscription_info.tier_id,
    )

    if (!availableTiers.includes(tier)) {
      tier = contractType === 'annual' || !campaignTierId ? 1 : campaignTierId
    }
  }
</script>

<div class="container">
  {#if subscriptions.length > 1}
    <div class="membership-select" bind:this={membershipSelect}>
      <ToggleButtonGroup block active={activeTierButton}>
        {#each subscriptions as subscription (subscription.id)}
          <Button
            rounded
            block
            variant="text"
            type="button"
            on:click={() => (tier = subscription.subscription_info.tier_id)}
          >
            {utils.toTierName(subscription.subscription_info.tier_name)}
          </Button>
        {/each}
      </ToggleButtonGroup>
    </div>
  {/if}
  <div class="membership-cards">
    {#each subscriptions as subscription (subscription.id)}
      {#if tierFeaturedStudios && imageUrl}
        <div
          role="button"
          class="membership-card"
          bind:this={membershipCard[subscription.subscription_info.tier_id]}
          tabindex="0"
          on:click={() => (tier = subscription.subscription_info.tier_id)}
          on:keydown={(e) => {
            if (e.key === 'Enter') {
              tier = subscription.subscription_info.tier_id
            }
          }}
          class:hidden={tier !== subscription.subscription_info.tier_id}
        >
          <MembershipCard
            {subscription}
            currency={currency || 'SEK'}
            {trial}
            {user}
            {campaign}
            {currentCity}
            {contractType}
          />
        </div>
      {:else}
        <div class:hidden={tier !== subscription.subscription_info.tier_id}>
          <MembershipCard
            {subscription}
            currency={currency || 'SEK'}
            {trial}
            {user}
            {campaign}
            {currentCity}
            {contractType}
          />
        </div>
      {/if}
    {/each}
  </div>
  {#if tierFeaturedStudios && imageUrl}
    <FeaturedStudioGrid
      title={`${STRINGS[lang].MEMBERSHIP_CARDS_FEATURED} ${selectedTier?.name} ${STRINGS[lang].MEMBERSHIP_CARDS_STUDIOS}`}
      {imageUrl}
      studios={tierFeaturedStudios}
      {arrowOffset}
      tierName={selectedTier?.name}
    />
  {/if}
</div>

<style lang="scss">
  @use '@bruce/assets/css/breakpoints';

  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;
    width: 100vw;
    --membership-card-max-width: 360px;

    .membership-select {
      display: none;

      @media screen and (max-width: breakpoints.$breakpoint-md) {
        display: flex;
        width: calc(100% - 2em);
      }
    }

    .membership-cards {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 2em;
      max-width: calc(var(--membership-card-max-width) * 3);
      width: 100%;

      @media screen and (max-width: breakpoints.$breakpoint-md) {
        display: flex;
        justify-content: center;
        width: calc(100% - 2em);
      }

      & > * {
        grid-column: span 2;
        width: 100%;

        &:first-child:nth-last-child(1) {
          grid-column: 3 / 5;
        }

        &:first-child:nth-last-child(2) ~ * {
          grid-column: 4 / 6;
        }

        &:first-child:nth-last-child(2) {
          grid-column: 2 / 4;
        }
      }

      .membership-card {
        cursor: pointer;
        max-width: var(--membership-card-max-width);
      }

      .hidden {
        @media screen and (max-width: breakpoints.$breakpoint-md) {
          display: none;
        }
      }
    }
  }
</style>
